import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLang } from '../context/LangContext';
import { FATWA_API, cachedFetch, fatwaNum, catIcon, trunc, fatwaScholarName } from '../utils/api';
import { invalidateCache } from '../utils/cache';

// Match count comes directly from Meilisearch via the API (match_count field)
function countMatches(fatwa) {
  return fatwa.match_count || 0;
}


function Pagination({ current, last, onPage }) {
  const pages=[...new Set([1,last,current,current-1,current+1].filter(p=>p>=1&&p<=last))].sort((a,b)=>a-b);
  const items=[]; let prev=0;
  for(const p of pages){
    if(p-prev>1)items.push(<button key={`e${p}`} className="pg-btn" disabled>…</button>);
    items.push(<button key={p} className={`pg-btn${p===current?' active':''}`} onClick={()=>onPage(p)}>{p}</button>);
    prev=p;
  }
  return <div className="pagination">{current>1&&<button className="pg-btn" onClick={()=>onPage(current-1)}>‹</button>}{items}{current<last&&<button className="pg-btn" onClick={()=>onPage(current+1)}>›</button>}</div>;
}


// ─── Category name helper ──────────────────────────────────────────────────
// Returns the localised name of a category object based on current lang.
// Falls back: translations[lang] → name_en → name_ar
function catName(c, lang) {
  if (!c) return '';
  if (lang === 'ar') return c.name_ar || '';
  // Check translations map from API
  if (c.translations && c.translations[lang] && c.translations[lang].name) {
    return c.translations[lang].name;
  }
  // Fallback to name_en for English, else Arabic
  if (lang === 'en') return c.name_en || c.name_ar || '';
  return c.name_ar || '';
}

// Given a fatwa `f` and the categories list, return the localised category label
function fatwaCatName(f, categories, lang, fallback = '') {
  if (!f) return fallback;
  const cat = (categories || []).find(c =>
    c.id === f.category_id ||
    c.name_ar === f.category_name ||
    c.name_en === f.category_name
  );
  if (cat) return catName(cat, lang);
  return f.category_name || fallback;
}

// ─── Language version helpers ─────────────────────────────────────────────
function _applyFatwa(f, lang) {
  const versions = f.versions || {};
  // For EN/UR: only use that language version, never fall back to Arabic
  const v = lang === 'ar'
    ? (versions.ar || {})
    : (versions[lang] || null);
  if (!v) return null; // no translation → exclude from list
  const q = v.question || v.full_text || '';
  return { ...f, question: q, title: q, answer: v.answer || v.full_text || '', full_text: v.full_text || '' };
}
function _filterFatwas(arr, lang) {
  return (arr || [])
    .map(f => _applyFatwa(f, lang))
    .filter(Boolean)
    .filter(f => f.question || f.answer || f.full_text);
}
// ──────────────────────────────────────────────────────────────────────────
export default function FatwaBankPage({ categories, scholars, lang }) {
  const { t } = useLang();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlSearch = searchParams.get('q') || '';
  const urlCat    = searchParams.get('cat') ? parseInt(searchParams.get('cat')) : null;
  const urlPage   = parseInt(searchParams.get('page')||'1');
  const urlSort   = searchParams.get('sort') || 'newest';
  const [results, setResults]     = useState([]);
  const [meta, setMeta]           = useState({current_page:1,last_page:1,total:0});
  const [loading, setLoading]     = useState(true);
  const [inputValue, setInputValue] = useState(urlSearch);
  const prevLang = useRef(lang);

  // When language switches: clear search + reset page
  useEffect(() => {
    if (prevLang.current !== lang) {
      prevLang.current = lang;
      setInputValue('');
      setSearchParams(prev => {
        const next = Object.fromEntries(prev.entries());
        delete next.q;
        delete next.page;
        return next;
      });
    }
  }, [lang]);

  // Keep input in sync with URL (e.g. browser back/forward)
  useEffect(() => { setInputValue(urlSearch); }, [urlSearch]);

  const updateParams = (changes) => {
    const next={...Object.fromEntries(searchParams.entries()),...changes};
    Object.keys(next).forEach(k=>{if(!next[k]||next[k]==='1')delete next[k];});
    setSearchParams(next);
  };

  const load = useCallback(async()=>{
    setLoading(true);
    const p=new URLSearchParams({page:urlPage});
    if(urlSearch)p.set('search',urlSearch);
    if(urlCat)p.set('category_id',urlCat);
    if(lang&&lang!=='ar')p.set('lang',lang);
    p.set('sort', urlSort);
    try{
      const url = `${FATWA_API}?${p}`;
      // Search results must never be served from cache — always fresh
      if(urlSearch) invalidateCache(url);
      const json=await cachedFetch(url);
      let data=_filterFatwas(json.data, lang);
      if(urlSearch){
        data=[...data].sort((a,b)=>countMatches(b)-countMatches(a));
      }
      setResults(data);
      setMeta({current_page:json.current_page||urlPage,last_page:json.last_page||1,total:json.total||data.length});
    }catch{setResults([]);}
    setLoading(false);
  },[lang,urlSearch,urlCat,urlPage,urlSort]);

  useEffect(()=>{load();},[load]);

  return (
    <div id="page-fatwa-bank">
      <div className="page-hero">
        <div className="geo-lg"/><div className="page-hero-glow"/>
        <div className="pw">
          <div className="breadcrumb">
            <a href="/" onClick={e=>{e.preventDefault();navigate('/');}}>
              {t('nav.home.label')}
            </a>
            <span>/</span><span>{t('fb.breadcrumb')}</span>
          </div>
          <h1>{t('fb.title1')} <em>{t('fb.title2')}</em></h1>
          <p>{t('fb.subtitle')}</p>
        </div>
      </div>
      <div className="pw">
        <div className="fatwa-page-grid">
          <aside>
            <div className="filter-box">
              <div className="filter-head">{t('fb.filter.title')}</div>
              <div className="filter-section">
                <h5>{t('fb.filter.search')}</h5>
                <input type="text" className="filter-search-input" placeholder={t('fb.filter.search.ph')}
                  value={inputValue}
                  onChange={e=>{
                    const val = e.target.value;
                    setInputValue(val);
                    clearTimeout(window._fst);
                    window._fst=setTimeout(()=>updateParams({q:val,page:null}),400);
                  }}/>
              </div>
              <div className="filter-section">
                <h5>{t('fb.filter.cat')}</h5>
                <div className="filter-option">
                  <input type="radio" name="cat" id="cat-all" checked={!urlCat} onChange={()=>updateParams({cat:null,page:null})}/>
                  <label htmlFor="cat-all">{t('fb.filter.cat.all')}</label>
                </div>
                {categories.map(c=>(
                  <div key={c.id} className="filter-option">
                    <input type="radio" name="cat" id={`cat-${c.id}`} checked={urlCat===c.id} onChange={()=>updateParams({cat:c.id,page:null})}/>
                    <label htmlFor={`cat-${c.id}`}>{catName(c, lang)}</label>
                  </div>
                ))}
              </div>
            </div>
          </aside>
          <div className="fatwa-results">
            <div className="fatwa-result-count">
              <span>{loading ? t('fb.loading') : `${meta.current_page}/${meta.last_page} · ${meta.total}`}</span>
              <select className="sort-select" value={urlSort} onChange={e=>updateParams({sort:e.target.value,page:null})}>
                <option value="newest">{t('fb.sort.newest')}</option>
                <option value="oldest">{t('fb.sort.oldest')}</option>
                {urlSearch && <option value="relevant">{t('fb.sort.relevant')}</option>}
              </select>
            </div>
            {loading ? (
              Array(5).fill(0).map((_,i)=>(
                <div key={i} className="fatwa-full-card" style={{pointerEvents:'none'}}>
                  <div className="ffc-body" style={{flex:1}}>
                    <div className="skel skel-tag"/>
                    <div className="skel skel-line" style={{width:'85%',marginTop:'.45rem'}}/>
                    <div className="skel skel-footer"/>
                  </div>
                </div>
              ))
            ) : results.length===0 ? (
              <div style={{textAlign:'center',padding:'3rem',color:'var(--ink3)'}}>
                <div style={{fontSize:'2.5rem',marginBottom:'.75rem'}}>🔍</div>
                <p>{t('fb.no.results')}</p>
                <button className="btn-primary" style={{marginTop:'1rem'}}
                  onClick={()=>{setSearchParams({});}}>
                  {t('fb.clear.filters')}
                </button>
              </div>
            ) : (
              <>
                {results.map(f=>(
                  <div key={f.id} className="fatwa-full-card" onClick={()=>navigate(`/fatwas/${f.id}`)} style={{cursor:'pointer'}}>

                    <div className="ffc-body">
                      <div style={{display:'flex',alignItems:'center',gap:'.5rem',flexWrap:'wrap'}}>
                        <span className="ffc-tag">{catIcon(f.category_name,categories)} {fatwaCatName(f, categories, lang, t('label.general'))}</span>
                        {urlSearch && (() => {
                          const count = countMatches(f);
                          return count > 0 ? (
                            <span style={{
                              display:'inline-flex',alignItems:'center',gap:'.25rem',
                              background:'var(--accent,#1a6b3c)',color:'#fff',
                              fontSize:'.7rem',fontWeight:700,borderRadius:'999px',
                              padding:'.15rem .55rem',whiteSpace:'nowrap'
                            }}>
                              🔍 {count} {count === 1 ? 'مرة' : 'مرات'}
                            </span>
                          ) : null;
                        })()}
                      </div>
                      <div className="ffc-q">{trunc(f.question||f.title,170)}</div>
                      {(f.answer||f.full_text)&&<div className="ffc-preview">{trunc(f.answer||f.full_text,190)}</div>}
                      <div className="ffc-footer">
                        <span className="scholar"
                          onClick={e=>{e.stopPropagation();const s=scholars.find(x=>x.id===f.researcher_id)||scholars.find(x=>x.name===f.scholar);if(s)navigate(`/scholars/${s.id}`);}}>
                          👤 {fatwaScholarName(f, scholars, lang, t('label.scholar'))}
                        </span>
                        <button className="ffc-read-btn"
                          onClick={e=>{e.stopPropagation();navigate(`/fatwas/${f.id}`);}}>
                          {t('fb.read.fatwa')}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                {meta.last_page>1&&<Pagination current={meta.current_page} last={meta.last_page} onPage={p=>updateParams({page:p})}/>}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}